import { P } from "common/components/Paragraph";
import { ITableColSpanProps } from "common/components/Table";
import { TableRowContext } from "common/components/Table/TableRowProvider";
import React, { useContext, useEffect } from "react";
import { DateTimeFormatter } from "common/components/DateTimeFormatter";
import { Nullable } from "common/types";
import { useAppTracking } from "common/hooks/storeHooks";
export interface ITableDataContentProps {
  content?: Nullable<string | Date>;
  children?: React.ReactNode;
  includeTime?: boolean;
  isDate?: boolean;
  defaultPadding?: boolean;
}

export interface ITableDataImageProps {
  imgSrc: string;
  imgAlt: string;
  content: string;
  defaultPadding?: boolean;
}
export interface ITableDataCheckboxProps {
  isSelected: boolean;
  onChange: (checked: boolean) => void;
  /**
   * Accessible label forwarded to the underlying `<input type="checkbox">`. The visible icon
   * is purely decorative, so callers should pass this when the row's checkbox needs a unique
   * label for assistive tech (e.g., per-row select in a multi-select table).
   */
  ariaLabel?: string;
  defaultPadding?: boolean;
}

export type ITableDataProps = ITableColSpanProps &
  (ITableDataContentProps | ITableDataImageProps | ITableDataCheckboxProps);

export const Td: React.FC<ITableDataProps> = (props) => {
  // Constants
  const alignClassName = props.align === "start" ? "items-start" : "items-center";
  const ownClassName = `flex h-full overflow-hidden ${alignClassName} gap-sm leading-4 ${props.defaultPadding !== false ? "px-lg" : ""} ${props.colSpanClasses ?? ""}`;
  const inputClassName = "flex items-center gap-xs text-secondary-900 peer-enabled:cursor-pointer";
  const labelClassName = "flex w-fit";

  const { setHasImage } = useContext(TableRowContext);
  const { trackEvent } = useAppTracking();

  // Type Guards
  const isImageProps = (props: ITableDataProps): props is ITableDataImageProps => "imgSrc" in props;
  const isContentProps = (props: ITableDataProps): props is ITableDataContentProps => "content" in props;
  const isCheckboxProps = (props: ITableDataProps): props is ITableDataCheckboxProps => "isSelected" in props;

  // Methods
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    trackEvent({
      action: "checkBoxChanged",
      category: "CheckBox",
      checkBoxType: "selected",
      value: checked,
    });
    (props as ITableDataCheckboxProps).onChange(checked);
  };

  useEffect(() => {
    if (isImageProps(props)) {
      setHasImage(true);
    }
  }, []);

  return (
    <div className={ownClassName}>
      {isImageProps(props) && (
        <img className="max-h-full max-w-full object-contain" src={props.imgSrc} alt={props.imgAlt} />
      )}
      {isContentProps(props) && (
        <TdContent>
          {"includeTime" in props || props.isDate ? (
            <DateTimeFormatter dateString={props.content} includeTime={props.includeTime} />
          ) : (
            <>{props.content as React.ReactNode}</>
          )}
        </TdContent>
      )}
      {"children" in props && props.children}
      {isCheckboxProps(props) && (
        <>
          <label className={labelClassName}>
            <input
              type="checkbox"
              className="hidden peer"
              checked={props.isSelected}
              onChange={handleChange}
              aria-label={props.ariaLabel}
            />
            <div className={inputClassName}>
              <span
                className={`material-symbols-outlined text-[22px] ${
                  props.isSelected ? "filled text-secondary-900" : "text-secondary-300"
                }`}>
                {props.isSelected ? "check_box" : "check_box_outline_blank"}
              </span>
            </div>
          </label>
        </>
      )}
    </div>
  );
};

export const TdContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <P className="text-secondary-900 break-words max-w-full">{children}</P>;
};
